import React, { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../lib/i18n.jsx";
import { Button } from "../components/ui/button";
import { Plus, CheckCircle2, CloudUpload, UserPlus } from "lucide-react";
import ReportModal from "./WorkerReportModal";
import WorkerMonth from "./WorkerMonth";
import { PendingForMe } from "../components/Movements";
import ShiftCard from "../components/ShiftCard";
import { cachedGet, getQueue, onQueueChange, syncQueue } from "../lib/offline";
import { onEvent } from "../lib/realtime";

export default function WorkerHome() {
  const { user } = useAuth();
  const { t, tn } = useI18n();
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState("");
  const [workDate, setWorkDate] = useState(new Date().toISOString().slice(0, 10));
  const [dayItems, setDayItems] = useState([]);
  const [modal, setModal] = useState(false); // false | "self" | "other"
  const { can } = useAuth();
  const [refresh, setRefresh] = useState(0);
  const [queue, setQueue] = useState(getQueue());
  const [wtypes, setWtypes] = useState([]);
  const [units, setUnits] = useState([]);
  useEffect(() => onQueueChange(setQueue), []);
  useEffect(() => { cachedGet("/config/work-types").then(setWtypes).catch(() => {}); cachedGet("/config/units").then(setUnits).catch(() => {}); }, []);
  const wName = (it) => tn(it.work_name) || tn(wtypes.find((w) => w.id === it.work_type_id)?.name) || "…";
  const uName = (it) => tn(it.unit_name) || tn(units.find((u) => u.id === it.unit_id)?.name) || "";

  const loadDay = useCallback(async () => {
    try {
      const { data } = await api.get("/reports");
      const items = [];
      for (const r of data.filter((x) => x.date === workDate && x.employee_id === user?.id).slice(0, 5)) {
        const full = await api.get(`/reports/${r.id}`);
        full.data.items.forEach((it) => items.push(it));
      }
      setDayItems(items);
    } catch {}
  }, [workDate, user?.id]);

  useEffect(() => onEvent((ev) => {
    if (ev.type === "config.changed" && ev.payload?.entity === "projects") cachedGet("/projects").then((data) => { setProjects(data); setProjectId((p) => data.some((x) => x.id === p) ? p : (data[0]?.id || "")); }).catch(() => {});
    if (ev.type === "employee.changed") cachedGet("/projects").then(setProjects).catch(() => {});
    if (ev.type?.startsWith("report.")) { loadDay(); setRefresh((r) => r + 1); }
  }), [loadDay]);
  useEffect(() => {
    cachedGet("/projects").then((data) => {
      setProjects(data);
      setProjectId((p) => p || data[0]?.id || "");
    }).catch(() => {});
  }, []);
  useEffect(() => { loadDay(); }, [loadDay]);

  const hour = new Date().getHours();
  const greet = hour < 12 ? t("good_morning") : user?.name;

  return (
    <div className="max-w-md mx-auto space-y-5">
      <div>
        <h1 className="font-display text-2xl font-bold">{greet}, {user?.first_name || user?.name}!</h1>
        <p className="text-[var(--text-3)] text-sm">{new Date().toLocaleDateString()}</p>
      </div>
      <PendingForMe />

      {queue.length > 0 && (
        <button data-testid="pending-sync" onClick={() => syncQueue()} className="w-full tactical-card p-3 flex items-center gap-2 text-sm border-[var(--amber)]">
          <CloudUpload size={18} className="text-[var(--amber)]" />
          <span className="flex-1 text-left">{t("pending_sync")}: <b>{queue.length}</b></span>
          <span className="text-[var(--amber)] font-semibold">{t("sync_now")}</span>
        </button>
      )}

      <div className="tactical-card p-4 grid grid-cols-1 min-[380px]:grid-cols-2 gap-3">
        <div>
          <label className="text-xs uppercase tracking-wider text-[var(--text-3)] font-semibold">{t("work_date")}</label>
          <input data-testid="worker-work-date" type="date" value={workDate} max={new Date().toISOString().slice(0, 10)} onChange={(e) => setWorkDate(e.target.value)}
            className="w-full mt-1 h-12 bg-[var(--bg)] border border-[var(--border)] rounded-lg px-2 text-base font-semibold" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-[var(--text-3)] font-semibold">{t("project")}</label>
          <select data-testid="worker-project-select" value={projectId} onChange={(e) => setProjectId(e.target.value)}
            className="w-full mt-1 h-12 bg-[var(--bg)] border border-[var(--border)] rounded-lg px-2 text-base font-semibold">
            {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>
      </div>

      <ShiftCard date={workDate} projectId={projectId} onSaved={() => setRefresh((r) => r + 1)} />

      <div className="tactical-card p-4">
        <div className="text-xs uppercase tracking-wider text-[var(--text-3)] font-semibold mb-3">{t("today_done")} · {workDate}</div>
        {dayItems.length === 0 ? (
          <div className="text-[var(--text-3)] text-sm py-3 text-center">{t("no_data")}</div>
        ) : (
          <div className="space-y-2">
            {dayItems.map((it) => (
              <div key={it.id} className="flex items-center gap-2 text-sm min-w-0">
                <CheckCircle2 size={16} className="text-[var(--green)] shrink-0" />
                <span className="flex-1 min-w-0 break-words">{wName(it)}</span>
                <span className="font-mono-num font-semibold shrink-0 whitespace-nowrap">{it.quantity} {uName(it)}</span>
                {it.actual_minutes ? <span className="font-mono-num text-xs text-[var(--text-3)]">{it.actual_minutes}′</span> : null}
              </div>
            ))}
          </div>
        )}
      </div>

      <Button data-testid="add-work-btn" onClick={() => setModal("self")}
        className="w-full big-touch h-16 text-lg font-bold bg-[var(--amber)] hover:bg-[var(--amber-2)] text-white gap-2 rounded-2xl">
        <Plus size={22} /> {t("add_work")}
      </Button>
      {can("reports.create_for_others") && (
        <Button data-testid="add-work-for-btn" variant="outline" onClick={() => setModal("other")}
          className="w-full h-14 text-base font-bold border-2 border-[var(--amber)] text-[var(--amber)] gap-2 rounded-2xl">
          <UserPlus size={20} /> {t("create_report_for")}
        </Button>
      )}

      <WorkerMonth refreshKey={refresh} />

      {modal && (
        <ReportModal projectId={projectId} initialDate={workDate} forEmployee={modal === "other"} onClose={() => setModal(false)}
          onSaved={() => { setModal(false); loadDay(); setRefresh((r) => r + 1); }} />
      )}
    </div>
  );
}
